import React, { useEffect, useLayoutEffect, useRef, useMemo, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import without from 'lodash/without';
import DropIndicator from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner';
import {
	useEpicCardsState,
	useIsRankingEnabled,
} from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import { ISSUE_HIERARCHY_LEVEL_SUBTASK } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-react-use-resize-observer
import { useResizeObserver } from '@atlassian/jira-react-use-resize-observer/src/index.tsx';
import { useEpicFilters } from '@atlassian/jira-software-backlog-filters-store/src/controllers/filters/main.tsx';
import { runRecalculateSubscribers } from '@atlassian/jira-software-fast-virtual/src/services/use-element-offset/index.tsx';
import {
	type MeasurementCacheEntry,
	useVirtual,
} from '@atlassian/jira-software-fast-virtual/src/services/use-virtual/index.tsx';
import { useAutobatch } from '@atlassian/jira-software-react-scheduler/src/ui/autobatch/index.tsx';
import { useEvent } from '@atlassian/jira-software-react-use-event/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { UNASSIGNED_EPIC_ID, UNASSIGNED_EPIC_ISSUE_KEY } from '../../../../common/constants.tsx';
import { usePanelData } from '../../../../context/use-panel-data.tsx';
import { EpicEmptyState } from './empty-state/index.tsx';
import EpicCard from './epic-card/main.tsx';
import type { EpicCardListProps, EpicCardRendererProps } from './types.tsx';
import { useDragToRankEpic, useRankEpic } from './use-drag-to-rank-epic/index.tsx';

const EpicCardRenderer = ({
	epic,
	epicIsLoading,
	progress,
	estimation,
	index,
	epicFilters,
	isEditEnabled,
	selectedIssues,
	isDropAllowed,
	onClick,
	onCardDetailClick,
	onCreateIssueClick,
	onAssignEpic,
	setMeasure,
	setForceRemeasure,
	rankEpic,
	onRefetch,
}: EpicCardRendererProps) => {
	const autobatch = useAutobatch();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [isRankingEnabled] = useIsRankingEnabled();

	const epicId = String(epic.id);
	const epicFilterIsActive = epicFilters.includes(epicId);
	const isLoading = fg('prefetch-epics-data-backlog') ? epicIsLoading : epic.isLoading;

	const isEpicRankable = !isLoading && isEditEnabled;

	const { dragState, closestEdge } = useDragToRankEpic({
		ref: !isRankingEnabled ? null : containerRef,
		epicId,
		index,
		isDropAllowed,
		rankEpic,
	});

	const onResize = useEvent(() => {
		if (containerRef.current) {
			setForceRemeasure(containerRef.current);
		}
	});
	/*
	 * *Important note:* This must be called in this layout hook
	 * because we must call it *after* DOM height has been changed.
	 */
	useLayoutEffect(() => {
		autobatch(() => {
			if (containerRef.current) {
				setMeasure(containerRef.current);
			}
		});
	}, [containerRef, setMeasure, autobatch]);

	useResizeObserver({
		ref: containerRef,
		onResize,
	});

	const content = (isDragging = false, isDragHandle = false) => (
		<EpicCard
			isLoading={isLoading}
			isDragging={isDragging}
			isDragHandle={isDragHandle}
			isActive={epicFilterIsActive}
			selectedIssues={selectedIssues}
			epic={epic}
			progress={progress}
			estimation={estimation}
			isDropAllowed={isDropAllowed}
			onClick={onClick}
			onDetailClick={onCardDetailClick}
			onCreateIssueClick={onCreateIssueClick}
			onAssignEpic={onAssignEpic}
			onRefetch={onRefetch}
		/>
	);

	return (
		<>
			<Gap />
			<DraggableCard
				ref={containerRef}
				data-testid="software-backlog-panel.ui.epic.epic-panel.epic-card-list.draggable-epic-card"
				data-cardid={epicId}
				tabIndex={isEpicRankable ? -1 : undefined} // disable outline style from dnd
			>
				{content(dragState.type === 'dragging')}
				{closestEdge && <DropIndicator edge={closestEdge} />}
			</DraggableCard>

			{dragState.type === 'dragging' &&
				ReactDOM.createPortal(content(true, true), dragState.container)}
		</>
	);
};

const COLLAPSED_HEIGHT = 100;
const CARD_HEIGHTS_CACHE = new Map<number, MeasurementCacheEntry>();

export const EpicCardList = ({
	isEditEnabled,
	selectedIssues,
	onAssignEpic,
	onCardDetailClick,
	onCreateIssueClick,
	onRefetch,
}: EpicCardListProps) => {
	const [epicFilterState, { setEpicFilters }] = useEpicFilters();
	const epicFilters = useMemo(() => epicFilterState || [], [epicFilterState]);
	const [{ cards, cardsWithFullDetailsLoaded, loading }] = useEpicCardsState();
	const { boardId, isCompanyManaged } = usePanelData();

	const shouldShowNoEpicFilter = cards.length > 0;

	const [lastSelectedRowIndex, setLastSelectedRowIndex] = useState<number>(0);

	// At list level so objects are not created many times for each card.
	const rankEpic = useRankEpic(boardId, isCompanyManaged);

	const memoizedOnCardClick = useCallback(
		(id: number, isShiftKeyPressed: boolean) => {
			const epicIdAsString = String(id);
			const epicIsActiveCurrently = epicFilters.includes(epicIdAsString);
			const cardListAsStrings = cards.map((card) => String(card.epic.id));
			const fullCardList = shouldShowNoEpicFilter
				? [String(UNASSIGNED_EPIC_ID), ...cardListAsStrings]
				: cardListAsStrings;
			const selectedEpicRowIndex = fullCardList.findIndex((cardId) => cardId === epicIdAsString);

			if (isShiftKeyPressed && fg('backlog-epic-shift-click-select')) {
				let selectedEpics = [];
				if (lastSelectedRowIndex < selectedEpicRowIndex) {
					// Select all epics between the last selected epic and the current one
					selectedEpics = fullCardList.slice(lastSelectedRowIndex, selectedEpicRowIndex + 1);
				} else {
					selectedEpics = fullCardList.slice(
						selectedEpicRowIndex,
						epicFilters.includes(fullCardList[lastSelectedRowIndex])
							? lastSelectedRowIndex + 1
							: lastSelectedRowIndex,
					);
				}
				setEpicFilters(selectedEpics);
				return;
			}
			if (epicIsActiveCurrently) {
				setEpicFilters(without(epicFilters, epicIdAsString));
			} else {
				setEpicFilters([...epicFilters, epicIdAsString]);
			}
			fg('backlog-epic-shift-click-select') && setLastSelectedRowIndex(selectedEpicRowIndex);
		},
		[cards, epicFilters, lastSelectedRowIndex, setEpicFilters, shouldShowNoEpicFilter],
	);

	const isDropAllowed = useCallback(
		() =>
			!selectedIssues.some((issue) => issue?.typeHierarchyLevel === ISSUE_HIERARCHY_LEVEL_SUBTASK),
		[selectedIssues],
	);

	const cacheOptions = useMemo(
		() => ({
			cache: CARD_HEIGHTS_CACHE,
			getCacheKey: (index: number) => cards[index]?.epic.id ?? 'none',
		}),
		[cards],
	);
	const { rows, totalSize, reinitialize } = useVirtual({
		rowCount: cards.length,
		overscanHeight: 300,
		offsetTop: 0,
		cacheOptions,
		getDefaultRowSize: () => COLLAPSED_HEIGHT,
	});

	useEffect(() => {
		reinitialize();
		// We need cards in here to make sure the card list is recalculated when things change
	}, [reinitialize, cards]);

	useLayoutEffect(() => {
		runRecalculateSubscribers();
	}, [cards]);

	return (
		<Box>
			{shouldShowNoEpicFilter && (
				<EpicCard
					isActive={epicFilters.includes(String(UNASSIGNED_EPIC_ID))}
					selectedIssues={selectedIssues}
					isDropAllowed={isDropAllowed}
					epic={{
						id: UNASSIGNED_EPIC_ID,
						key: UNASSIGNED_EPIC_ISSUE_KEY,
						name: '',
						isLoading: false,
						hasError: false,
					}}
					progress={{
						status: {
							toDo: {
								issueCount: 0,
							},
							inProgress: {
								issueCount: 0,
							},
							done: {
								issueCount: 0,
							},
						},
						totalIssues: 0,
					}}
					estimation={null}
					onClick={memoizedOnCardClick}
					onCreateIssueClick={onCreateIssueClick}
					onAssignEpic={onAssignEpic}
					onRefetch={noop}
				/>
			)}
			<CardList $height={totalSize}>
				{rows.length > 0 &&
					rows.map(({ index, top, measure, forceRemeasure }) => {
						const card = cards[index];
						// Check that the card actually exists
						if (!card) {
							return null;
						}
						const { epic, progress, estimation } = card;
						const epicIsLoading = !cardsWithFullDetailsLoaded.has(epic.id);

						return (
							<Card top={top} key={`epic-card-${epic.id}`}>
								<EpicCardRenderer
									key={epic.id}
									index={index}
									isEditEnabled={isEditEnabled}
									selectedIssues={selectedIssues}
									epicFilters={epicFilters}
									epic={epic}
									epicIsLoading={epicIsLoading}
									progress={progress}
									estimation={estimation}
									isDropAllowed={isDropAllowed}
									onClick={memoizedOnCardClick}
									onCardDetailClick={onCardDetailClick}
									onCreateIssueClick={onCreateIssueClick}
									onAssignEpic={onAssignEpic}
									onRefetch={onRefetch}
									setMeasure={measure}
									setForceRemeasure={forceRemeasure}
									rankEpic={rankEpic}
								/>
							</Card>
						);
					})}
				{!loading && cards.length === 0 && <EpicEmptyState />}
				{loading && cards.length === 0 && fg('prefetch-epics-data-backlog') && (
					<Flex alignItems="center" justifyContent="center" xcss={loadingWrapper}>
						<Spinner size="medium" />
					</Flex>
				)}
			</CardList>
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardList = styled.div<{ $height: number }>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ $height }) => ($height > 0 ? `${$height}px` : 'auto'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Card = styled.div<{ top: number }>({
	position: 'absolute',
	left: token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	top: ({ top }) => `${top}px`,
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DraggableCard = styled.div({
	position: 'relative',
	/* remove the default dnd styling */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&[data-rbd-drag-handle-context-id]': {
		cursor: 'default',
	},
	paddingBottom: token('space.025'),
});

// gap in place where the in-between ICC should be normally
// 0.05 is small enough that the browser will round it down to 0
// but also large enough to be considered a non-zero value so it will prevent margin collapsing
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Gap = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingTop: '0.05px',
});

const loadingWrapper = xcss({
	paddingBlock: 'space.600',
});
